import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { buildWebFromArticleUrl } from '../services/graphApi';
import './Header.css';

const NAVBAR_COLLAPSE_ID = 'navbarNav';

function closeMobileNav() {
  const el = document.getElementById(NAVBAR_COLLAPSE_ID);
  if (!el || !el.classList.contains('show')) {
    return;
  }
  const Collapse = window.bootstrap?.Collapse;
  if (Collapse) {
    Collapse.getOrCreateInstance(el, { toggle: false }).hide();
    return;
  }
  el.classList.remove('show');
  const toggler = document.querySelector('.navbar-toggler[data-bs-target="#navbarNav"]');
  if (toggler) {
    toggler.setAttribute('aria-expanded', 'false');
    toggler.classList.add('collapsed');
  }
}

const READER_NAV = [
  { path: '/news', label: 'News', title: 'Platform AI newspaper (tabs + compose view)', matchPrefix: true },
  { path: '/desk', label: 'Desk', title: 'Live categorized intelligence board' },
  { path: '/web', label: 'Web', title: 'Memetic Spider — live entity graph explorer' },
  { path: '/feed', label: 'Feed', title: 'Searchable article archive' },
  { path: '/stringers', label: 'Stringers', title: 'Writer magazine and NewsAgent Shorts' },
];

/** Shown only when signed in (account holders). */
const ACCOUNT_NAV = [
  { path: '/authors', label: 'Authors', title: 'Browse writer author channels', matchPrefix: true },
  { path: '/research', label: 'Research', title: 'Private research workspace' },
];

/** Admin ops — configuration and pipeline (principalName === admin). */
const ADMIN_OPS_NAV = [
  { path: '/admin/feeds', label: 'Config', title: 'Admin configuration (feeds and site settings)', matchPrefix: true },
  { path: '/admin/pipeline-ops', label: 'Pipeline', title: 'Pipeline Ops — plow & entity quality', matchPrefix: true },
];

/** Admin legal — separated from ops by nav hairline. */
const ADMIN_LEGAL_NAV = [
  { path: '/admin/copyright', label: 'DMCA', title: 'Copyright / feed-removal requests', matchPrefix: true },
];

function resolveNavTo(path, pathname) {
  if (path !== '/web') {
    return path;
  }
  const match = pathname.match(/^\/article\/([^/]+)\/?$/);
  if (match?.[1]) {
    return buildWebFromArticleUrl(decodeURIComponent(match[1]), { layout: 'starburst' });
  }
  return '/web';
}

const Header = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, user, logout } = useAuth();
  const { theme, themes, setTheme } = useTheme();
  const [confirmLogout, setConfirmLogout] = useState(false);

  // React Router Links skip Bootstrap's collapse-on-select — close on navigate.
  useEffect(() => {
    closeMobileNav();
  }, [location.pathname]);

  const isNavActive = (path, matchPrefix = false) => {
    if (matchPrefix) {
      return location.pathname === path || location.pathname.startsWith(`${path}/`);
    }
    return location.pathname === path;
  };

  const handleLogin = () => {
    navigate('/login');
  };

  const handleLogout = async () => {
    if (!confirmLogout) {
      setConfirmLogout(true);
      return;
    }
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setConfirmLogout(false);
      navigate('/');
    }
  };

  const navGroups = [
    { id: 'reader', items: READER_NAV },
    ...(isAuthenticated ? [{ id: 'account', items: ACCOUNT_NAV }] : []),
    ...(user?.principalName === 'admin'
      ? [
          { id: 'admin-ops', items: ADMIN_OPS_NAV },
          { id: 'admin-legal', items: ADMIN_LEGAL_NAV },
        ]
      : []),
  ];

  return (
    <header className="header">
      <nav className="navbar navbar-expand-lg navbar-dark">
        <div className="container">
          <Link className="navbar-brand" to="/" onClick={closeMobileNav}>
            DarkNews
          </Link>

          <button
            className="navbar-toggler"
            type="button"
            data-bs-toggle="collapse"
            data-bs-target="#navbarNav"
            aria-controls="navbarNav"
            aria-expanded="false"
            aria-label="Toggle navigation"
          >
            <span className="navbar-toggler-icon"></span>
          </button>

          <div className="collapse navbar-collapse" id={NAVBAR_COLLAPSE_ID}>
            <ul className="navbar-nav me-auto">
              {navGroups.map((group, groupIndex) =>
                group.items.map(({ path, label, title, matchPrefix }, itemIndex) => (
                  <li
                    key={path}
                    className={`nav-item${groupIndex > 0 && itemIndex === 0 ? ' nav-item--group-start' : ''}`}
                  >
                    <Link
                      className={`nav-link ${isNavActive(path, matchPrefix) ? 'active' : ''}`}
                      to={resolveNavTo(path, location.pathname)}
                      title={title}
                      onClick={closeMobileNav}
                    >
                      {label}
                    </Link>
                  </li>
                )),
              )}
            </ul>

            <ul className="navbar-nav header-nav-end align-items-lg-center">
              <li className="nav-item header-theme">
                <label className="visually-hidden" htmlFor="header-theme-select">
                  Theme
                </label>
                <select
                  id="header-theme-select"
                  className="form-select form-select-sm header-theme-select"
                  value={theme}
                  onChange={(e) => setTheme(e.target.value)}
                  title={themes[theme]?.description}
                  aria-label="Theme"
                >
                  {Object.values(themes).map(({ id, label }) => (
                    <option key={id} value={id}>
                      {label}
                    </option>
                  ))}
                </select>
              </li>

              <li className="nav-item header-auth">
                {isAuthenticated ? (
                  <div className="dropdown">
                    <button
                      className="nav-link dropdown-toggle d-flex align-items-center"
                      type="button"
                      data-bs-toggle="dropdown"
                      aria-expanded="false"
                    >
                      {user?.displayName || user?.principalName || 'Profile'}
                    </button>
                    <ul className="dropdown-menu dropdown-menu-dark dropdown-menu-end">
                      <li>
                        <button className="dropdown-item" type="button" onClick={handleLogout}>
                          {confirmLogout ? 'Confirm sign out?' : 'Sign out'}
                        </button>
                      </li>
                    </ul>
                  </div>
                ) : (
                  <button className="btn btn-outline-primary" type="button" onClick={() => { closeMobileNav(); handleLogin(); }}>
                    Log in
                  </button>
                )}
              </li>
            </ul>
          </div>
        </div>
      </nav>
    </header>
  );
};

export default Header;
